import { computed, inject, signal, Service } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';

import { getCssColor } from '@ideals/utils/get-css-var';
import { getDomain } from '@ideals/utils/get-domain';
import { sendMessageToDevice } from '@ideals/utils/send-message-to-device';

export const THEME_NAMES = [
  'malachite',
  'emerald',
  'moderate-blue',
  'davy-grey',
  'dodger-blue',
  'intense-teal',
  'sunset-orange',
  'epicurean-orange',
  'beetroot',
] as const;

export type ThemeName = (typeof THEME_NAMES)[number];
export type ThemeMode = 'dark' | 'light';

const DEFAULT_THEME_NAME: ThemeName = 'malachite';
// NOTE need to get actual list of colors from BE and remove unused
const THEMES_REMAP: Record<string, ThemeName> = {
  '#ef5350': 'sunset-orange',
  '#ce1141': 'sunset-orange',
  '#c72027': 'sunset-orange',
  '#ec407a': 'sunset-orange',
  '#f4640c': 'epicurean-orange',
  '#ff7043': 'epicurean-orange',
  '#f7a738': 'epicurean-orange',
  '#e96a48': 'epicurean-orange',
  '#891157': 'beetroot',
  '#ab47bc': 'beetroot',
  '#3bae5b': 'emerald',
  '#8bc34a': 'emerald',
  '#4caf50': 'emerald',
  '#66bb6a': 'emerald',
  '#535353': 'davy-grey',
  '#3e3e3e': 'davy-grey',
  '#616161': 'davy-grey',
  '#8d6e63': 'davy-grey',
  '#4862d3': 'moderate-blue',
  '#4055b3': 'moderate-blue',
  '#536dfe': 'moderate-blue',
  '#9575cd': 'moderate-blue',
  '#5c6bc0': 'moderate-blue',
  '#2c9c74': 'malachite',
  '#009688': 'intense-teal',
  '#24998c': 'intense-teal',
  '#26a69a': 'intense-teal',
  '#26c6da': 'intense-teal',
  '#4894b0': 'intense-teal',
  '#607d8b': 'intense-teal',
  '#358ceb': 'dodger-blue',
  '#42a5f5': 'dodger-blue',
  '#6191ca': 'dodger-blue',
  '#29b6f6': 'dodger-blue',
  '#00a1cd': 'dodger-blue',
};
const THEME_DARK_KEY = 'ideals-theme-dark';

@Service()
export class ThemesService {
  readonly #cookieService = inject(CookieService);
  readonly #dark = signal(matchMedia('(prefers-color-scheme: dark)').matches);
  readonly #themeName = signal(DEFAULT_THEME_NAME);

  readonly dark = computed(() => this.#dark());
  readonly mode = computed(() => this.#dark() ? 'dark' : 'light');
  readonly themeName = computed(() => this.#themeName());

  colorToThemeName(color: string): ThemeName {
    if (THEME_NAMES.includes(color as ThemeName)) {
      return color as ThemeName;
    }

    const themeName = THEMES_REMAP[color.toLowerCase()];

    if (themeName) {
      return themeName;
    }

    console.warn(`Cannot find theme by color (${color}). Current theme is used.`);

    return this.#themeName();
  }

  initialize(): void {
    const isDark = this.#cookieService.check(THEME_DARK_KEY)
      ? JSON.parse(this.#cookieService.get(THEME_DARK_KEY)) as boolean
      : this.#dark();

    this.#dark.set(isDark);

    this.#updateTheme();
    this.#updateDeviceThemeBackground();
    this.#updateDeviceThemeColor();
  }

  restoreDefault(): void {
    this.setThemeName(DEFAULT_THEME_NAME);
  }

  setColorScheme(dark: boolean): void {
    if (dark === this.#dark()) {
      return;
    }

    this.#dark.set(dark);

    const expires = new Date();

    expires.setFullYear(expires.getFullYear() + 1);
    this.#cookieService.set(THEME_DARK_KEY, JSON.stringify(dark), { path: '/', domain: getDomain(), expires });

    this.#updateTheme();
    this.#updateDeviceThemeBackground();
  }

  setThemeName(name: ThemeName): void {
    if (name === this.themeName()) {
      return;
    }

    this.#themeName.set(name);

    this.#updateTheme();
    this.#updateDeviceThemeColor();
  }

  themeNameToClass(name: ThemeName): string {
    return `ideals-theme-${name}`;
  }

  #updateDeviceThemeBackground(): void {
    const color = getCssColor('ideals-main-bg');
    let metaElement = document.head.querySelector('meta[name="theme-color"]');

    if (!metaElement) {
      metaElement = document.head.appendChild(document.createElement('meta'));
      metaElement.setAttribute('name', 'theme-color');
    }

    metaElement.setAttribute('content', color);
    document.body.style.backgroundColor = color;
    sendMessageToDevice('changeThemeBackground', color);
  }

  #updateDeviceThemeColor(): void {
    sendMessageToDevice('changeThemeColor', getCssColor('ideals-primary-500'));
  }

  #updateTheme(): void {
    const element = document.documentElement;

    this.#updateThemeMode(element, this.dark());

    THEME_NAMES.forEach((themeName) => {
      element.classList.remove(this.themeNameToClass(themeName));
    });

    element.classList.add(this.themeNameToClass(this.#themeName()), 'ideals-no-theme-transition');

    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    getComputedStyle(element).width; // force reflow
    element.classList.remove('ideals-no-theme-transition');
  }

  #updateThemeMode(element: HTMLElement, dark: boolean): void {
    const targetTheme = dark ? 'ideals-theme-dark' : 'ideals-theme-light';
    const oldTheme = dark ? 'ideals-theme-light' : 'ideals-theme-dark';

    if (!element.classList.contains(targetTheme)) {
      element.classList.remove(oldTheme);
      element.classList.add(targetTheme);
    }
  }
}
